import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthService, pinRefusedMessage } from '../auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-home',
  imports: [ReactiveFormsModule],
  standalone: true,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  loginForm: FormGroup;
  error: string | null = null;
  showSecret = false;
  submitting = false;
  readonly year = new Date().getFullYear();

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    if (this.authService.isLoggedIn()) {
      const role = this.authService.getRole();
      this.router.navigate([this.getDefaultRouteForRole(role)]);
    }
    this.loginForm = this.fb.group({
      identifier: ['', [Validators.required]],
      secret: ['', Validators.required],
    });
  }

  /**
   * Get the default route for a given user role
   * @param role - The user's role
   * @returns The default route path for the role
   */
  private getDefaultRouteForRole(role: string): string {
    const roleRouteMap: { [key: string]: string } = {
      'Admin': '/dashboard',
      ADMIN: '/dashboard',
      SUPER_ADMIN: '/dashboard',
      'CASHIER': '/billing',
      'Cashier': '/billing',
      'STORE_MANAGER': '/billing',
      'AREA_MANAGER': '/billing',
      'ASM': '/billing',
      'WAREHOUSE_MANAGER': '/dashboard',
      'CATEGORY_MANAGER': '/dashboard',
      'MIS_TEAM': '/dashboard',
      'GRN_TEAM': '/grn',
      'PRN_TEAM': '/prn',
      'LP_TEAM': '/product-list',
      'INWARD_TEAM': '/transfer-in',
      'OUTWARD_TEAM': '/transfer-out',
      'StoreManager': '/billing',
      'WarehouseManager': '/transfer-out',
    };

    // Platform level: a super admin without a workspace starts on Workspaces.
    if (this.authService.isSuperAdmin() && !this.authService.getSelectedTenantId()) {
      return '/super-admin/clients';
    }
    return roleRouteMap[role] || '/dashboard'; // Every role can open the overview
  }

  onSubmit() {
    if (this.loginForm.valid && !this.submitting) {
      this.error = null;
      this.submitting = true;
      this.authService.login(this.loginForm.value).subscribe({
        next: () => {
          this.authService.getLoggedInUser().subscribe({
            next: (user: any) => {
              localStorage.setItem('user', JSON.stringify(user));
              const roleName = user.roles?.[0]?.role?.name;
              if (roleName) {
                localStorage.setItem('role', roleName);
              }
              const userRole = this.authService.getRole(); // Get role from AuthService

              // Multi-store users must pick which store/warehouse to work
              // in before landing anywhere stock-related.
              if ((user.storeAccess?.length ?? 0) > 1) {
                this.router.navigate(['/select-store']);
                return;
              }

              // Redirect based on role using the mapping
              const defaultRoute = this.getDefaultRouteForRole(userRole);
              this.router.navigate([defaultRoute]);
            },
            error: () => {
              this.submitting = false;
              this.error = 'Failed to fetch user details';
            },
          });
        },
        error: (err) => {
          this.submitting = false;
          this.error =
            err?.status === 401
              ? pinRefusedMessage(err) ?? 'Invalid username or password.'
              : err?.status === 0
                ? 'Cannot reach the server. Check your connection and try again.'
                : 'Login failed. Please try again.';
        },
      });
    }
  }
}
